/**
 * Investigations port — the index of collaborative investigation case files.
 *
 * An investigation is a watch scope (what the team is following) plus a case
 * file (pinned evidence, shared notes, live discussion). Its *discussion and
 * notes* live in the collaborative Yjs room, synced across the team; everything
 * durable — title, watches, retention, pinned evidence, archive state — is
 * persisted here.
 *
 * **Backed by saved views.** An investigation is a `SavedView` at
 * `tableId: "investigations"`: `name` is the title, `scope` maps Personal /
 * Shared, `state.extra` carries the {@link InvestigationState}, and
 * `state.pinnedRows` the pinned evidence. That is what makes a case visible to
 * teammates at all — the previous `localStorage` index meant a colleague opening
 * `/investigations` saw nothing, and sharing depended on pasting a link. Saved
 * views are already server-side, tenancy-scoped and shareable, so this needs no
 * new backend.
 *
 * Watch shapes are validated on read: a hand-edited or older `state.extra`
 * cannot put a malformed watch into the feed's query fan-out.
 */
import type { SavedView, SavedViewsSource, ViewScope } from "@authe/saved-views";
import { isTxTenancyScopeId, type TxTenancyScopeId } from "@/features/transactions/scopes";
import type { TimeRangePreset } from "@authe/fe-commons/metadata-analytics";
import { parseSceneOverlay, type SceneOverlayDTO } from "@authe/fe-commons/models";
import {
  DEFAULT_INVESTIGATION_STATE,
  DEFAULT_RETENTION,
  FEED_WINDOW_PRESETS,
  WINDOW_ALL,
  parseAccount,
  type FeedWindow,
  type InvestigationSaga,
  type InvestigationState,
  type InvestigationStatus,
  type Retention,
  type Watch,
} from "@/features/investigations/model";

/** The saved-view surface investigations live on. */
export const INVESTIGATIONS_TABLE_ID = "investigations";

/** Personal (`user`) vs Shared with the tenant (`tenant`). */
export type InvestigationScope = ViewScope;

export interface Investigation extends InvestigationState {
  id: string;
  title: string;
  scope: InvestigationScope;
  /** Auth0 sub of the creator, when the backend recorded one. */
  createdBy: string | null;
  /** Canonical ids of transactions pinned as evidence (exempt from retention). */
  pinned: string[];
  createdAt: number;
  updatedAt: number;
}

export interface CreateInvestigationInput {
  title: string;
  scope: InvestigationScope;
  watches?: Watch[];
  retention?: Partial<Retention>;
  saga?: InvestigationSaga;
}

export interface InvestigationsSource {
  /** Every investigation the caller can see, newest activity first. */
  list(): Promise<Investigation[]>;
  get(id: string): Promise<Investigation | null>;
  create(input: CreateInvestigationInput): Promise<Investigation>;
  update(investigation: Investigation): Promise<Investigation>;
  remove(id: string): Promise<void>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter((v): v is string => v !== null) : [];
}

/**
 * Validate one persisted watch, dropping anything malformed.
 *
 * A watch is turned straight into query parameters and a live predicate, so an
 * unusable one is worse than absent: `{ kind: "address" }` with no account would
 * fan out a request with an empty `addresses` filter and quietly return the
 * whole unfiltered tenant feed into a case file that claims to be scoped.
 */
function parseWatch(raw: unknown): Watch | null {
  if (!isRecord(raw)) return null;
  const kind = str(raw.kind);
  switch (kind) {
    case "protocol": {
      const protocolId = str(raw.protocolId);
      if (!protocolId) return null;
      const chains = strList(raw.chains);
      return chains.length ? { kind, protocolId, chains } : { kind, protocolId };
    }
    case "address": {
      const account = str(raw.account);
      if (!account) return null;
      const chains = strList(raw.chains);
      if (chains.length) return { kind, account, chains };
      // Legacy shape: a boolean `crossChain` where `false` meant "only the
      // account's own chain". Migrated on read rather than left to the `chains`
      // default, which would silently widen an intentionally chain-scoped watch
      // to its whole namespace and pull unrelated chains into a saved case.
      if (raw.crossChain === false) {
        const own = parseAccount(account).chain;
        if (own) return { kind, account, chains: [own] };
      }
      return { kind, account };
    }
    case "chain": {
      const chain = str(raw.chain);
      return chain ? { kind, chain } : null;
    }
    case "entity":
    case "tag":
    case "label":
    case "group": {
      const catalogId = str(raw.catalogId);
      if (!catalogId) return null;
      const name = str(raw.name);
      return name ? { kind, catalogId, name } : { kind, catalogId };
    }
    default:
      return null;
  }
}

/**
 * Read a persisted feed window, accepting the legacy preset-string form.
 *
 * Investigations saved before the window became a shape carry a bare
 * `window: "1h" | "24h" | "7d" | "all"`. Those are still meaningful, so they are
 * translated rather than reset — silently dropping a saved case's window back to
 * the default would change what an existing investigation shows the next time
 * someone opens it.
 */
function parseFeedWindow(raw: unknown): FeedWindow {
  if (typeof raw === "string") {
    if (raw === "all") return WINDOW_ALL;
    return FEED_WINDOW_PRESETS.includes(raw as TimeRangePreset)
      ? { kind: "rolling", preset: raw as TimeRangePreset }
      : DEFAULT_RETENTION.window;
  }
  if (!isRecord(raw)) return DEFAULT_RETENTION.window;
  if (raw.kind === "all") return WINDOW_ALL;
  if (raw.kind === "rolling") {
    return FEED_WINDOW_PRESETS.includes(raw.preset as TimeRangePreset)
      ? { kind: "rolling", preset: raw.preset as TimeRangePreset }
      : DEFAULT_RETENTION.window;
  }
  if (raw.kind === "absolute") {
    const since = Number(raw.since);
    const until = Number(raw.until);
    // Both bounds required and ordered: a half-written absolute window would
    // otherwise read as "since epoch", quietly turning a saved historical range
    // into an unbounded scan of the whole partition.
    if (Number.isFinite(since) && Number.isFinite(until) && since > 0 && since <= until) {
      return { kind: "absolute", since: Math.floor(since), until: Math.floor(until) };
    }
  }
  return DEFAULT_RETENTION.window;
}

function parseRetention(raw: unknown): Retention {
  if (!isRecord(raw)) return DEFAULT_RETENTION;
  const maxRows = Number(raw.maxRows);
  return {
    window: parseFeedWindow(raw.window),
    maxRows: Number.isFinite(maxRows) && maxRows > 0 ? Math.floor(maxRows) : DEFAULT_RETENTION.maxRows,
    // Only an explicit `false` turns it off, so a case saved before the field
    // existed gets the default rather than the falsy reading of `undefined`.
    balanceChains: raw.balanceChains !== false,
  };
}

function parseSaga(raw: unknown): InvestigationSaga {
  return raw === "bridge.ccip" || raw === "bridge.cctp" ? raw : null;
}

function parseStatus(raw: unknown): InvestigationStatus {
  return raw === "archived" ? "archived" : "open";
}

/**
 * The feed's tenancy tier, defaulted for cases saved before it was a field.
 *
 * Those cases were pinned to their visibility scope (`user` or `tenant`), so
 * inheriting `view.scope` here would faithfully reproduce an empty feed. They
 * get the default instead.
 */
function parseFeedScope(raw: unknown): TxTenancyScopeId {
  return isTxTenancyScopeId(raw) ? raw : DEFAULT_INVESTIGATION_STATE.feedScope;
}

/**
 * The graph annotation layer, or `null` when the case has none.
 *
 * Validated like the watches are, and for a sharper reason: this body is folded
 * onto every scene the case renders, so a hand-edited or older `state.extra`
 * that no longer matches the schema would take the graph down rather than just
 * degrade it. A layer we can't read is dropped — the observed graph still draws.
 */
function parseAnnotations(raw: unknown): SceneOverlayDTO | null {
  if (!isRecord(raw)) return null;
  try {
    return parseSceneOverlay(raw);
  } catch {
    return null;
  }
}

/**
 * The case's table layout — a flat `{ columnId: visible }` map, or `null` when
 * nobody has chosen one. Non-boolean entries are dropped rather than coerced: a
 * junk value would silently hide a column, and no layout at all falls back to
 * the focus default, which is a better answer than a wrong one.
 */
function parseColumnVisibility(raw: unknown): Record<string, boolean> | null {
  if (!isRecord(raw)) return null;
  const columns: Record<string, boolean> = {};
  for (const [id, visible] of Object.entries(raw)) {
    if (typeof visible === "boolean") columns[id] = visible;
  }
  return Object.keys(columns).length > 0 ? columns : null;
}

function investigationFromView(view: SavedView): Investigation {
  const extra = isRecord(view.state?.extra) ? view.state.extra : {};
  const watches = Array.isArray(extra.watches)
    ? extra.watches.map(parseWatch).filter((w): w is Watch => w !== null)
    : [];
  const lastActivityAt = Number(extra.lastActivityAt);
  return {
    id: view.id,
    title: view.name || "Untitled investigation",
    scope: view.scope,
    createdBy: view.owner ?? null,
    watches,
    retention: parseRetention(extra.retention),
    feedScope: parseFeedScope(extra.feedScope),
    status: parseStatus(extra.status),
    saga: parseSaga(extra.saga),
    annotations: parseAnnotations(extra.annotations),
    columnVisibility: parseColumnVisibility(extra.columnVisibility),
    lastActivityAt: Number.isFinite(lastActivityAt) ? lastActivityAt : view.updatedAt,
    pinned: strList(view.state?.pinnedRows),
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
  };
}

function investigationToView(investigation: Investigation): SavedView {
  const { id, title, scope, createdBy, pinned, createdAt, updatedAt, ...state } = investigation;
  return {
    id,
    name: title,
    tableId: INVESTIGATIONS_TABLE_ID,
    scope,
    owner: createdBy,
    state: { pinnedRows: pinned, extra: { ...state } },
    createdAt,
    updatedAt,
  };
}

function byActivityDesc(a: Investigation, b: Investigation): number {
  return Math.max(b.lastActivityAt, b.updatedAt) - Math.max(a.lastActivityAt, a.updatedAt);
}

/**
 * The investigations index over a {@link SavedViewsSource}.
 *
 * `list()` asks for both scopes in one call (the saved-views list endpoint
 * returns personal + shared when `scope` is omitted) so the page can group them
 * without a second round trip.
 */
export function createSavedViewInvestigationsSource(
  views: SavedViewsSource,
): InvestigationsSource {
  const listAll = async (): Promise<Investigation[]> => {
    const rows = await views.list({ tableId: INVESTIGATIONS_TABLE_ID });
    return rows.map(investigationFromView).sort(byActivityDesc);
  };

  return {
    list: listAll,
    async get(id) {
      // Saved views expose no by-id read, so this reads through the list. Cheap
      // in practice (a tenant has tens of cases, not thousands) and it keeps the
      // port free of a second backend contract.
      return (await listAll()).find((row) => row.id === id) ?? null;
    },
    async create(input) {
      const now = Date.now();
      const draft: SavedView = {
        id: "",
        name: input.title.trim() || "Untitled investigation",
        tableId: INVESTIGATIONS_TABLE_ID,
        scope: input.scope,
        state: {
          pinnedRows: [],
          extra: {
            ...DEFAULT_INVESTIGATION_STATE,
            watches: input.watches ?? [],
            retention: { ...DEFAULT_RETENTION, ...input.retention },
            saga: input.saga ?? null,
            lastActivityAt: now,
          },
        },
        createdAt: now,
        updatedAt: now,
      };
      return investigationFromView(await views.create(draft));
    },
    async update(investigation) {
      const next = { ...investigation, lastActivityAt: Date.now() };
      return investigationFromView(await views.update(investigationToView(next)));
    },
    async remove(id) {
      await views.remove(id);
    },
  };
}
